import { fdeEntryApi, incomeVerificationApi } from 'src/api';
import { defaultInstance } from 'src/api/instance';
import { getConfigVars } from 'src/helpers';
import { IncomeVerification } from 'src/ts/interfaces/incomeVerification';
import { CalculationDocument } from './constants';
import { CalculateIncomeOverrideDocument, ManualOverrideRequest } from './ts/interfaces';

const apiUrl = getConfigVars(window.location.hostname).api_url;

export type CalculateIncomeOverrideResponse = {
  data?: {
    results?: {
      monthly_income: number;
      yearly_income: number;
      daily_income: number;
      consecutive_days: number;
      reason_codes: number[];
      reference_id?: string;
      status_code?: string;
    };
    reference_id?: string;
  };
};

// ITL-2708 — types for the specialty-calculation endpoint. Hand-rolled because
// the SDK can't be regenerated (see CLAUDE.md).
export type SpecialtyCalculationDocument = {
  document_id: string;
  income_source: string;
  gross_income: number | string;
  document_start_date: string;
  document_end_date: string;
  source_type?: string;
};

export type SpecialtyCalculation = {
  reference_id?: string;
  monthly_income?: number | string;
  source_type?: string;
  documents: SpecialtyCalculationDocument[];
};

export type PreviousNote = {
  text: string;
  author_name: string | null;
  created_at: string;
};

export type CalculationNoteOrigin = 'SPECIALTY' | 'DISPUTE';

export type CalculationNote = PreviousNote & { origin: CalculationNoteOrigin };

export type CalculationNotesResponse = { data?: CalculationNote[] };

export type SpecialtyCalculationResponse = {
  data?: {
    calculation?: SpecialtyCalculation | null;
    previous_note?: PreviousNote | null;
  };
};

export type CalculationDocumentsResponse = {
  data?: {
    calculation_id: string | null;
    documents: CalculationDocument[];
    previous_note: PreviousNote | null;
    note_required: boolean;
  };
};

const api = {
  calculateIncome: (submissionId: string, documents: CalculateIncomeOverrideDocument[]) => {
    return fdeEntryApi.calculateIncome(submissionId, documents);
  },
  sendReport: (submissionId: string, documents: IncomeVerification[]) => {
    return incomeVerificationApi.completeIncomeVerificationReview(submissionId, documents);
  },
  appealCalculation: (entryId: string) => {
    return incomeVerificationApi.assignIncomeVerificationDispute(entryId);
  },

  getAppealDocuments: async (entryId: string) => {
    return incomeVerificationApi.showIncomeVerificationDispute(entryId);
  },
  overrideIncomeVerification: async (entryId: string, payload: ManualOverrideRequest) => {
    return defaultInstance.post(
      `${apiUrl}/income_verifications/submission/${entryId}/review/overwrite`,
      payload
    );
  },
  // ITL-2933 — completes the specialty review and releases the report with no income
  // value (no override recorded). Hand-rolled: the SDK can't be regenerated (CLAUDE.md).
  // ITL-2953 — reason_code records why no additional income was calculated.
  completeSpecialtyWithoutIncome: async (entryId: string, reasonCode: number) => {
    return defaultInstance.post(
      `${apiUrl}/income_verifications/submission/${entryId}/review/complete-specialty`,
      { reason_code: reasonCode }
    );
  },
  calculateIncomeOverride: async (
    entryId: string,
    documents: CalculateIncomeOverrideDocument[]
  ): Promise<CalculateIncomeOverrideResponse> => {
    return defaultInstance.post(
      `${apiUrl}/entries/${entryId}/calculate_income_override`,
      documents
    );
  },
  getCalculationDocuments: async (entryId: string): Promise<CalculationDocumentsResponse> => {
    return defaultInstance.get(
      `${apiUrl}/income_verifications/submission/${entryId}/review/calculation-documents`
    );
  },
  getCalculationNotes: async (entryId: string): Promise<CalculationNotesResponse> => {
    return defaultInstance.get(
      `${apiUrl}/income_verifications/submission/${entryId}/review/calculation-notes`
    );
  },
  overrideCalculation: async (entryId: string, calculationId: string, note?: string) => {
    return defaultInstance.post(
      `${apiUrl}/income_verifications/submission/${entryId}/review/override-calculation`,
      { calculation_id: calculationId, note }
    );
  },
  getAutomatedProofs: async (entryId: string) => {
    return defaultInstance.get(
      `${apiUrl}/income_verifications/submission/${entryId}/review/automated_proofs`
    );
  },
  // ITL-2708 — Specialty Income Calc page. Backend endpoint lands in Phase 1 (Elixir).
  // Until then this returns a 404 from the server; the page treats that as the empty state.
  getSpecialtyCalculation: async (entryId: string): Promise<SpecialtyCalculationResponse> => {
    return defaultInstance.get(
      `${apiUrl}/income_verifications/submission/${entryId}/review/specialty-calculation`
    );
  }
};

export default api;
